import { useRef, useEffect, useCallback } from 'react'

// Shared access to thebe, without the leaks.
//
// thebe is loaded as one global (see index.html) whose event bus and cell list are
// module-level singletons, and every thebelab.bootstrap() call requests a brand-new
// kernel. Pages used to call bootstrap() from two or three separate effects, so a single
// page visit started two or three kernels -- and whichever became ready last won the
// kernelRef, while the visible cell's run button was bound to a different one. thebe's
// "status" event bus is global too, so a status handler registered by one page fired for
// every other page's kernel (including kernels from another binder repo/language) and
// kept firing after that page had unmounted.
//
// usePageKernel gives each mounted component its own kernel -- one per component
// instance, not one per effect -- and shuts it down again when the component unmounts,
// so pages keep the separate R/Python namespaces they have always had without piling
// up abandoned kernels on the binder server.

// thebe remembers the binder server url+token in localStorage and, on the next load,
// commits to it after a single probe. mybinder.org culls idle servers after ~10 minutes,
// so that probe can still pass against a server that dies moments later -- and thebe has
// no fallback once it has committed, it just rejects. Dropping the remembered entry lets
// a retry go through a fresh binder build instead.
const forgetSavedSession = (config) => {
    const repo = (config.binderOptions || {}).repo
    try {
        Object.keys(window.localStorage)
            .filter((key) => key.startsWith('thebe-binder') && (!repo || key.includes(repo)))
            .forEach((key) => window.localStorage.removeItem(key))
    } catch (e) {
        // private mode / storage disabled -- nothing cached, nothing to forget
    }
}

const hasSavedSession = (config) => {
    const repo = (config.binderOptions || {}).repo
    try {
        return Object.keys(window.localStorage)
            .some((key) => key.startsWith('thebe-binder') && (!repo || key.includes(repo)))
    } catch (e) {
        return false
    }
}

// Reusing a live binder server takes a moment; a cold build legitimately takes minutes.
// So this bound only ever applies to the saved-session path, never to a fresh build.
const SAVED_SESSION_TIMEOUT_MS = 20000

// jQuery-object-or-array -> array of {cell, execute, setOutputText}
const cellList = (cells) => {
    if (!cells) return []
    return typeof cells.toArray === 'function' ? cells.toArray() : Array.from(cells)
}

const resolveCells = (cells, kernel) => {
    cellList(cells).forEach((entry) => {
        if (!entry || !entry.cell) return
        const resolve = thebelab.$(entry.cell).data('kernel-promise-resolve')
        if (resolve) resolve(kernel)
    })
}

const startKernel = (config) => {
    // Never throw synchronously. usePageKernel's getter is called from inside effects, and
    // an exception escaping a passive effect -- with no error boundary above these pages --
    // tears down the whole React root, so the app renders as a blank page instead of one
    // page reporting a failure. `thebelab` is a global set by a <script> tag in index.html
    // (unpkg.com/thebe@0.8.2), so it can legitimately be absent if that request failed.
    if (typeof thebelab === 'undefined' || !thebelab || typeof thebelab.bootstrap !== 'function') {
        return Promise.reject(new Error(
            'thebe did not load -- check the unpkg.com/thebe script tag in index.html'
        ))
    }

    let attempt = Promise.resolve().then(() => thebelab.bootstrap(config))

    if (hasSavedSession(config)) {
        // thebe validates a remembered server with a single request and no timeout of its
        // own, so a server that has since been culled but whose route still resolves
        // leaves bootstrap() pending forever -- the page sits on "Loading..." with nothing
        // in the console and the retry below never gets a rejection to react to. Treat a
        // long wait on this path as a dead cache and fall through to a fresh build.
        let timer
        const expiry = new Promise((_, reject) => {
            timer = setTimeout(
                () => reject(new Error('saved binder session did not respond in time')),
                SAVED_SESSION_TIMEOUT_MS
            )
        })
        const clearTimer = (value) => { clearTimeout(timer); return value }
        const clearTimerAndRethrow = (err) => { clearTimeout(timer); throw err }
        attempt = Promise.race([attempt.then(clearTimer, clearTimerAndRethrow), expiry])
    }

    return attempt.catch((err) => {
        console.warn('Thebe kernel request failed; retrying with a fresh binder session', err)
        forgetSavedSession(config)
        // bootstrap() has already replaced the page's <pre data-executable> elements with
        // rendered cells, so re-running it would render nothing and leave those cells
        // waiting for a kernel forever. Request just the kernel and hand it to the cells
        // the first attempt rendered.
        const rendered = thebelab.cells
        return thebelab.requestBinderKernel({
            binderOptions: { ...(config.binderOptions || {}) },
            kernelOptions: { ...(config.kernelOptions || {}) },
        }).then((kernel) => {
            resolveCells(rendered, kernel)
            return kernel
        })
    })
}

/**
 * This component's own kernel. Returns a stable getter: call it from as many effects as
 * you like and they all share the one kernel this component started -- the first call
 * starts it, later calls get the same promise back. The kernel is shut down when the
 * component unmounts.
 */
export function usePageKernel(config) {
    const promiseRef = useRef(null)

    const getPageKernel = useCallback(() => {
        if (!promiseRef.current) {
            const started = startKernel(config)
            promiseRef.current = started
            started.then((kernel) => {
                if (!kernel || !kernel.statusChanged
                    || typeof kernel.statusChanged.connect !== 'function') return
                // A dead kernel can never execute again -- jupyterlab throws
                // "Kernel is dead" out of requestExecute. Drop it so the next caller
                // starts a fresh one rather than being handed a corpse.
                kernel.statusChanged.connect((_sender, status) => {
                    if (status === 'dead' && promiseRef.current === started) {
                        promiseRef.current = null
                    }
                })
            }).catch(() => {})
        }
        return promiseRef.current
    }, [])

    useEffect(() => {
        // Nothing is going to use this kernel again, and leaving it running holds memory on
        // a binder pod that only has ~2GB and one CPU for every page the user visits.
        const shutdown = () => {
            const pending = promiseRef.current
            promiseRef.current = null
            if (pending) {
                pending
                    .then((kernel) => kernel && kernel.shutdown && kernel.shutdown())
                    .catch(() => {})
            }
        }

        // A page refresh or tab close never runs React effect cleanups, so on its own the
        // cleanup below would leak one kernel per refresh onto the pod. Best effort only:
        // the shutdown request may not finish before the page goes away.
        //
        // pagehide rather than beforeunload: beforeunload also fires for navigations that
        // are then cancelled, which would kill the kernel out from under a page that is
        // still on screen -- the next run would fail with "Kernel is dead". persisted
        // means the page went into the back/forward cache and can still come back, so its
        // kernel has to survive that too.
        const onPageHide = (event) => {
            if (!event || !event.persisted) shutdown()
        }
        window.addEventListener('pagehide', onPageHide)

        return () => {
            window.removeEventListener('pagehide', onPageHide)
            shutdown()
        }
    }, [])

    return getPageKernel
}

/**
 * Render any <pre data-executable> currently in the DOM and connect it to `kernel`.
 *
 * Pages that rebuild their code element whenever the generated code changes used to call
 * bootstrap() again to get it rendered, which also requested another kernel. Rendering is
 * all they actually needed: renderAllCells() consumes the <pre> and replaces it with a
 * thebe cell, which then just needs the kernel this page already has.
 */
export function attachCells(kernel) {
    // The selector must be passed explicitly. thebe 0.8.2's renderAllCells() defaults it
    // to `_defaultOptions.selector`, but render.js only imports mergeOptions from
    // ./options and never imports _defaultOptions -- so calling it without a selector
    // throws "ReferenceError: _defaultOptions is not defined" (render.js:258). thebe's
    // own bootstrap() always passes options.selector; do the same.
    const selector = (typeof thebelab.getOption === 'function' && thebelab.getOption('selector'))
        || '[data-executable]'
    const cells = thebelab.renderAllCells({ selector })
    if (cellList(cells).length === 0) return
    resolveCells(cells, kernel)
    // keep thebe's own cell list (used by "restart & run all") on the live cells
    thebelab.cells = cells
}
